import { useState, useEffect } from "react";
import {
  MathOperations, Equals, Sparkle, Robot, PaperPlaneRight, Package, Plus, Scales,
  DotsThreeVertical, PencilSimple, TrashSimple, Copy, Palette, Stack, ListChecks, FloppyDisk, CaretDown,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, downloadBlob } from "../lib/api";
import { fileColor, verifyColor } from "../lib/files";
import { FormulaBuilder, runFormula } from "./FormulaBuilder";

const CATS = [
  { id: "contabilita", label: "Contabilità", Icon: MathOperations, color: "cyan" },
  { id: "confronto", label: "Confronto", Icon: Equals, color: "neon" },
  { id: "dati", label: "Dati", Icon: Stack, color: "magenta" },
  { id: "formattazione", label: "Formattazione", Icon: Palette, color: "violet" },
];

const TOOLS = [
  { key: "difference", cat: "contabilita", name: "Differenza", desc: "A − B tra due colonne numeriche",
    fields: [{ k: "col_a", t: "single", label: "Colonna A" }, { k: "col_b", t: "single", label: "Colonna B" }] },
  { key: "sum_column", cat: "contabilita", name: "Totale Colonna", desc: "Somma totale (verticale) di una o più colonne",
    fields: [{ k: "columns", t: "multi", label: "Colonne" }] },
  { key: "row_sum", cat: "contabilita", name: "Somma righe", desc: "Somma per riga di 2+ colonne (es. A + B su ogni riga)",
    fields: [{ k: "columns", t: "multi", label: "Colonne da sommare" }] },
  { key: "sum_compare", cat: "contabilita", name: "Confronto somme", desc: "Somma N colonne A vs N colonne B",
    fields: [{ k: "cols_a", t: "multi", label: "Colonne A" }, { k: "cols_b", t: "multi", label: "Colonne B" }] },
  { key: "group_sum_diff", cat: "contabilita", name: "Differenza per gruppo", desc: "Somma A vs B raggruppata per una chiave",
    fields: [{ k: "group_key", t: "single", label: "Colonna gruppo" }, { k: "cols_a", t: "multi", label: "Colonne A" }, { k: "cols_b", t: "multi", label: "Colonne B" }] },
  { key: "date_group_sum", cat: "contabilita", name: "Totali per data", desc: "Somma colonne per giorno, quindicina, mese o anno con riga di totale",
    fields: [{ k: "date_col", t: "single", label: "Colonna data" }, { k: "columns", t: "multi", label: "Colonne da sommare" },
      { k: "unit", t: "choice", label: "Periodo", default: "month", options: [["day", "Giorno"], ["fortnight", "Quindicina (1-15 / 16-fine)"], ["month", "Mese"], ["year", "Anno"]] }] },
  { key: "value", cat: "confronto", name: "Confronto valore", desc: "OK / ≠ tra due colonne",
    fields: [{ k: "col_a", t: "single", label: "Colonna A" }, { k: "col_b", t: "single", label: "Colonna B" }] },
  { key: "equal_label", cat: "confronto", name: "Uguale / Diverso", desc: "Etichetta Uguale o Diverso",
    fields: [{ k: "col_a", t: "single", label: "Colonna A" }, { k: "col_b", t: "single", label: "Colonna B" }] },
  { key: "contains", cat: "confronto", name: "Contiene testo", desc: "Sì / No se la colonna contiene un testo",
    fields: [{ k: "col", t: "single", label: "Colonna" }, { k: "value", t: "text", label: "Testo da cercare" }] },
  { key: "dedup_flag", cat: "dati", name: "Trova duplicati", desc: "Segna Duplicato / Unico sulle colonne chiave",
    fields: [{ k: "columns", t: "multi", label: "Colonne chiave" }] },
  { key: "conditional_format", cat: "formattazione", name: "Evidenzia colonna", desc: "Colora una colonna (verde/arancio/rosso) in base ai valori",
    fields: [{ k: "col", t: "single", label: "Colonna da evidenziare" }] },
];

const PRESETS = [
  { slug: "perc", name: "Percentuale (A / B × 100)", explanation: "Calcola la percentuale di A rispetto a B", category: "contabilita",
    spec: { op: "percentage", params: {}, new_col: "_percentuale" },
    fields: [{ k: "left", t: "single", label: "Colonna A (numeratore)" }, { k: "right", t: "single", label: "Colonna B (denominatore)" }] },
  { slug: "scarto", name: "Scarto % (A vs B)", explanation: "Differenza percentuale (A−B)/B×100", category: "contabilita",
    spec: { op: "expression", params: { expr: "(col('{{a}}') - col('{{b}}')) / col('{{b}}') * 100", vtype: "num" }, new_col: "_scarto_pct" },
    fields: [{ k: "a", t: "single", label: "Colonna A" }, { k: "b", t: "single", label: "Colonna B" }] },
  { slug: "round2", name: "Arrotonda a 2 decimali", explanation: "Arrotonda una colonna numerica a 2 decimali", category: "contabilita",
    spec: { op: "round", params: { decimals: 2 }, new_col: "_arrotondato" },
    fields: [{ k: "col", t: "single", label: "Colonna da arrotondare" }] },
  { slug: "merge", name: "Unisci colonne", explanation: "Unisce più colonne in una (separatore spazio)", category: "dati",
    spec: { op: "merge_columns", params: { separator: " " }, new_col: "_unione" },
    fields: [{ k: "source_cols", t: "multi", label: "Colonne da unire" }] },
  { slug: "affix", name: "Aggiungi prefisso/suffisso", explanation: "Aggiunge un testo prima e/o dopo i valori di una colonna", category: "dati",
    spec: { op: "concat_constant", params: {}, new_col: "_etichetta" },
    fields: [{ k: "source_col", t: "single", label: "Colonna" }, { k: "prefix", t: "text", label: "Prefisso" }, { k: "suffix", t: "text", label: "Suffisso" }] },
  { slug: "normalize", name: "Normalizza testo", explanation: "Minuscolo e spazi ridotti, utile prima dei confronti", category: "dati",
    spec: { op: "normalize", params: { mode: "text" }, new_col: "_normalizzato" },
    fields: [{ k: "col", t: "single", label: "Colonna" }] },
];

const CAT_COLOR = Object.fromEntries(CATS.map((c) => [c.id, c.color]));
const GLOW = { cyan: "glow-cyan", magenta: "glow-magenta", neon: "glow-neon", gold: "glow-cyan", violet: "glow-magenta" };
const TEXT = { cyan: "text-[hsl(var(--cyan))]", magenta: "text-[hsl(var(--magenta))]", neon: "text-[hsl(var(--neon))]", gold: "text-[hsl(var(--gold))]", violet: "text-[hsl(265,80%,60%)]" };

function colStyle(c) { return c.file > 0 ? { color: fileColor(c.file) } : (c.verify ? { color: verifyColor(c.key) } : {}); }

function SingleColSelect({ value, onChange, columns, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-muted" data-testid={testid}><SelectValue placeholder="Scegli colonna" /></SelectTrigger>
      <SelectContent className="bg-popover border-border max-h-72">
        {columns.map((c) => <SelectItem key={c.key} value={c.key} className="grid-font text-xs" style={colStyle(c)}>{c.label}{c.file > 0 ? ` · F${c.file}` : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function MultiColSelect({ value = [], onChange, columns, testid }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start bg-muted font-normal" data-testid={testid}>
          {value.length ? `${value.length} colonne selezionate` : "Scegli colonne"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="bg-popover border-border w-72 p-0">
        <ScrollArea className="h-64 p-3">
          <div className="space-y-1.5">
            {columns.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-xs py-1 cursor-pointer" data-testid={`${testid}-opt-${c.key}`}>
                <Checkbox checked={value.includes(c.key)} onCheckedChange={() => toggle(c.key)} />
                <span className="grid-font" style={colStyle(c)}>{c.label}{c.file > 0 ? ` · F${c.file}` : ""}</span>
              </label>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function FieldControl({ field, value, onChange, columns }) {
  const testid = `tool-field-${field.k}`;
  if (field.t === "choice") return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-muted" data-testid={testid}><SelectValue placeholder="Scegli" /></SelectTrigger>
      <SelectContent className="bg-popover border-border">
        {field.options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-opt-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (field.t === "multi") return <MultiColSelect value={value} onChange={onChange} columns={columns} testid={testid} />;
  if (field.t === "text" || field.t === "number")
    return <Input type={field.t === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-muted" data-testid={testid} placeholder={field.label} />;
  return <SingleColSelect value={value} onChange={onChange} columns={columns} testid={testid} />;
}

function ToolDialog({ tool, open, setOpen, columns, onRun, activeCount }) {
  const [params, setParams] = useState({});
  useEffect(() => {
    if (tool) {
      const init = {};
      (tool.fields || []).forEach((f) => { if (f.default !== undefined) init[f.k] = f.default; });
      setParams(init);
    }
  }, [tool]);
  const set = (k, v) => setParams((p) => ({ ...p, [k]: v }));
  const ready = tool && (tool.fields || []).every((f) => {
    if (f.t === "multi") return (params[f.k] || []).length > 0;
    if (f.t === "single") return !!params[f.k];
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setParams({}); }}>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          <ListChecks size={22} weight="duotone" className="text-[hsl(var(--accent))]" /> {tool?.name}
        </DialogTitle>
        <DialogDescription>{(tool?.desc || tool?.explanation) || ""}{(tool?.desc || tool?.explanation) ? " · " : ""}si applica a <strong>{activeCount}</strong> {activeCount === 1 ? "riga attiva" : "righe attive"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(tool?.fields || []).map((f) => (
            <div key={f.k}>
              <label className="text-xs text-muted-foreground mb-1 block">{f.label || f.k}</label>
              <FieldControl field={f} value={params[f.k]} onChange={(v) => set(f.k, v)} columns={columns} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button disabled={!ready} data-testid="run-tool-btn"
            onClick={() => { onRun(params); setOpen(false); setParams({}); }}>
            Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryLine({ summary }) {
  const entries = Object.entries(summary).filter(([k]) => k !== "dettaglio" && k !== "tool" && k !== "column" && k !== "tabella");
  return (
    <div className="text-xs grid-font bg-[hsl(var(--cyan)/0.08)] border border-[hsl(var(--cyan)/0.3)] rounded-lg px-3 py-2 text-[hsl(190,90%,30%)]">
      <span className="font-semibold">✓ {summary.tool}</span> — {entries.map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join("+") : v}`).join(" · ")}
    </div>
  );
}

export function VerificationPlayground({ job, data, setData, activeRowIds }) {
  const [active, setActive] = useState(null); // {kind:'factory'|'ai', def}
  const [open, setOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [feed, setFeed] = useState([]);
  const [modifyTool, setModifyTool] = useState(null);
  const [modifyText, setModifyText] = useState("");
  const [renameTool, setRenameTool] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [library, setLibrary] = useState([]);
  const [saveLibTool, setSaveLibTool] = useState(null);
  const [saveLibName, setSaveLibName] = useState("");
  const [presetBusy, setPresetBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set(["contabilita", "confronto", "dati", "formattazione", "gallery", "ai", "library"]));
  const toggleWrap = (id) => setCollapsed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const isWrapOpen = (id) => !collapsed.has(id);
  const uid = typeof window !== "undefined" ? localStorage.getItem("rowtool_uid") : null;
  const columns = data.columns || [];
  const pickColumns = [...columns.filter((c) => c.verify), ...columns.filter((c) => !c.verify && !c.technical)];
  const aiTools = data.ai_tools || [];
  const rowIds = activeRowIds && activeRowIds.length ? activeRowIds : null;
  const activeCount = rowIds ? rowIds.length : (data.results || []).filter((r) => !r._subtotal).length;

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const matches = (...vals) => !q || vals.some((v) => (v || "").toString().toLowerCase().includes(q));
  const galleryPresets = PRESETS.filter((p) => matches(p.name, p.explanation));
  const aiToolsF = aiTools.filter((t) => matches(t.name, t.explanation));
  const libraryF = library.filter((t) => matches(t.name, t.tool?.explanation, t.tool?.name));

  const loadLibrary = async () => {
    try { const res = await api.listTemplates(uid); setLibrary((res || []).filter((t) => t.type === "tool")); } catch (e) {}
  };
  useEffect(() => { loadLibrary(); }, []);

  const saveToLibrary = async () => {
    if (!saveLibName.trim() || !saveLibTool) return;
    try { await api.saveToolTemplate(uid, saveLibName.trim(), saveLibTool); toast.success("Tool salvato in libreria"); loadLibrary(); }
    catch (e) { toast.error("Errore salvataggio"); }
    setSaveLibTool(null); setSaveLibName("");
  };
  const importFromLibrary = async (t) => {
    try { const res = await api.importTool(job.id, t.tool); setData({ ...data, ai_tools: res.ai_tools }); toast.success(`"${t.name}" importato`); }
    catch (e) { toast.error("Errore importazione"); }
  };
  const deleteFromLibrary = async (t) => {
    try { await api.deleteTemplate(t.id); loadLibrary(); toast.message("Rimosso dalla libreria"); }
    catch (e) { toast.error("Errore"); }
  };

  const applyResult = (res) => {
    setData({ ...data, results: res.results, columns: res.columns, verifications: res.verifications || data.verifications });
    setFeed((f) => [{ type: "verify", summary: res.summary }, ...f]);
    toast.success("Strumento applicato");
  };

  const runFactory = async (tool, params) => {
    try {
      const res = await api.verify(job.id, tool.key, params, rowIds);
      applyResult(res);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore verifica"); }
  };

  const runAiApply = async (tool, params) => {
    try {
      const res = await api.applyAiTool(job.id, tool.id, params, rowIds);
      if (res.result_type === "zip") {
        const blob = await api.split(job.id, res.split_by, { file_format: res.file_format, index: res.index, index_fields: res.index_fields, index_aggs: res.index_aggs });
        downloadBlob(blob, `${job.name}_split.zip`);
        toast.success("ZIP generato e scaricato");
      } else {
        applyResult(res);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore tool"); }
  };

  const runPreset = async (preset, params) => {
    if (presetBusy) return;
    setPresetBusy(true);
    try {
      const existing = (data.ai_tools || []).find((t) => t.name === preset.name);
      let toolId = existing?.id;
      let newAiTools = data.ai_tools || [];
      if (!existing) {
        const imp = await api.importTool(job.id, preset);
        toolId = imp.tool.id;
        newAiTools = imp.ai_tools;
      }
      const res = await api.applyAiTool(job.id, toolId, params, rowIds);
      if (res.result_type === "zip") {
        const blob = await api.split(job.id, res.split_by, { file_format: res.file_format, index: res.index, index_fields: res.index_fields, index_aggs: res.index_aggs });
        downloadBlob(blob, `${job.name}_split.zip`);
        setData({ ...data, ai_tools: newAiTools });
        toast.success("ZIP generato e scaricato");
      } else {
        setData({ ...data, ai_tools: newAiTools, results: res.results, columns: res.columns, verifications: res.verifications || data.verifications });
        setFeed((f) => [{ type: "verify", summary: res.summary }, ...f]);
        toast.success(existing ? "Tool consigliato applicato" : "Tool consigliato applicato e aggiunto ai tuoi tool");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore tool consigliato"); }
    finally { setPresetBusy(false); }
  };

  const onDialogRun = (params) => {
    if (!active) return;
    if (active.kind === "factory") runFactory(active.def, params);
    else if (active.kind === "preset") runPreset(active.def, params);
    else runAiApply(active.def, params);
  };

  const openTool = (kind, def) => {
    const fields = def.fields || [];
    if (fields.length === 0 && kind === "ai") { runAiApply(def, {}); return; }
    setActive({ kind, def });
    setOpen(true);
  };

  const applyFormula = async (formula, newCol) => {
    setAiLoading(true);
    try {
      const res = await runFormula(job, formula, newCol, rowIds);
      if (res) { applyResult(res); setAiText(""); }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore formula"); }
    finally { setAiLoading(false); }
  };

  const runAI = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    const request = aiText;
    setFeed((f) => [{ type: "user", text: request }, ...f]);
    setAiText("");
    try {
      const res = await api.aiTool(job.id, request);
      setData({ ...data, ai_tools: res.ai_tools });
      setFeed((f) => [{ type: "ai", text: `Creato "${res.tool.name}" — clicca il pulsante per scegliere le colonne e applicarlo`, }, ...f]);
      toast.success("Tool AI creato");
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore tool AI";
      setFeed((f) => [{ type: "ai", text: msg, error: true }, ...f]);
      toast.error(msg);
    } finally { setAiLoading(false); }
  };

  const submitModify = async () => {
    if (!modifyText.trim() || !modifyTool) return;
    setAiLoading(true);
    const tid = modifyTool.id;
    setModifyTool(null);
    try {
      const res = await api.aiTool(job.id, modifyText, tid);
      setData({ ...data, ai_tools: res.ai_tools });
      toast.success("Tool aggiornato dall'AI");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore modifica"); }
    finally { setAiLoading(false); setModifyText(""); }
  };

  const submitRename = async () => {
    if (!renameVal.trim() || !renameTool) return;
    try {
      const res = await api.renameAiTool(job.id, renameTool.id, renameVal.trim());
      setData({ ...data, ai_tools: res.ai_tools });
      toast.success("Tool rinominato");
    } catch (e) { toast.error("Errore rinomina"); }
    setRenameTool(null); setRenameVal("");
  };

  const deleteTool = async (tool) => {
    try { const res = await api.deleteAiTool(job.id, tool.id); setData({ ...data, ai_tools: res.ai_tools }); toast.message("Tool eliminato"); }
    catch (e) { toast.error("Errore eliminazione"); }
  };

  const duplicateTool = async (tool) => {
    try { const res = await api.duplicateAiTool(job.id, tool.id); setData({ ...data, ai_tools: res.ai_tools }); toast.success("Tool duplicato"); }
    catch (e) { toast.error("Errore duplicazione"); }
  };

  return (
    <section data-testid="verification-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="stage-num" style={{ background: "hsl(var(--magenta))" }}>4</span>
        <h2 className="text-xl font-head font-bold">Playground verifiche</h2>
        <Sparkle size={18} weight="fill" className="text-[hsl(var(--gold))]" />
        <span className="ml-auto text-xs text-muted-foreground" data-testid="active-scope">
          Gli strumenti si applicano a <strong>{activeCount}</strong> {activeCount === 1 ? "riga attiva" : "righe attive"}{rowIds && activeCount < (data.results || []).filter((r) => !r._subtotal).length ? " (selezione/filtro)" : ""}
        </span>
      </div>

      <div className="mb-4">
        <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="tool-search"
          placeholder="Cerca uno strumento..."
          className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]" />
      </div>

      {CATS.map((cat) => {
        const tools = TOOLS.filter((t) => t.cat === cat.id && matches(t.name, t.desc));
        if (!tools.length) return null;
        const isOpen = searching ? true : !collapsed.has(cat.id);
        return (
          <div key={cat.id} className="mb-5" data-testid={`cat-${cat.id}`}>
            <button onClick={() => setCollapsed((s) => { const n = new Set(s); if (n.has(cat.id)) n.delete(cat.id); else n.add(cat.id); return n; })}
              data-testid={`cat-toggle-${cat.id}`}
              className={`w-full text-xs font-semibold mb-2 flex items-center gap-1.5 ${TEXT[cat.color]}`}>
              <cat.Icon size={15} weight="duotone" /> {cat.label}
              <CaretDown size={12} className={`ml-1 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
            </button>
            {isOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {tools.map((t) => (
                  <button key={t.key} onClick={() => openTool("factory", t)} data-testid={`tool-${t.key}`}
                    className={`tool-card metallic text-left rounded-xl border border-border p-4 hover:${GLOW[cat.color]}`}>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2" title={t.desc}>{t.desc}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {(!searching || galleryPresets.length > 0) && (
      <div className="mb-6" data-testid="gallery-section">
        <button onClick={() => toggleWrap("gallery")} data-testid="wrap-gallery"
          className="w-full text-xs font-semibold text-[hsl(var(--gold))] mb-2 flex items-center gap-1.5">
          <Sparkle size={15} weight="fill" /> Consigliati · pronti all'uso
          <CaretDown size={12} className={`ml-1 transition-transform ${(searching || isWrapOpen("gallery")) ? "" : "-rotate-90"}`} />
        </button>
        {(searching || isWrapOpen("gallery")) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {galleryPresets.map((p) => (
            <button key={p.slug} onClick={() => openTool("preset", p)} data-testid={`gallery-tool-${p.slug}`}
              className="tool-card metallic text-left rounded-xl border border-[hsl(var(--gold)/0.45)] p-3 hover:glow-cyan">
              <div className="flex items-center gap-1.5 font-semibold text-sm">
                <Sparkle size={14} weight="fill" className="text-[hsl(var(--gold))]" /> {p.name}
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2">{p.explanation}</div>
            </button>
          ))}
        </div>
        )}
      </div>
      )}

      {aiTools.length > 0 && (!searching || aiToolsF.length > 0) && (
        <div className="mb-6" data-testid="ai-tools-list">
          <button onClick={() => toggleWrap("ai")} data-testid="wrap-ai"
            className="w-full text-xs font-semibold text-[hsl(var(--magenta))] mb-2 flex items-center gap-1"><Robot size={14} /> Tool creati con l'AI
            <CaretDown size={12} className={`ml-1 transition-transform ${(searching || isWrapOpen("ai")) ? "" : "-rotate-90"}`} />
          </button>
          {(searching || isWrapOpen("ai")) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aiToolsF.map((t) => (
              <div key={t.id} data-testid={`ai-tool-${t.id}`}
                className="tool-card rounded-xl border border-[hsl(var(--magenta)/0.5)] bg-[hsl(var(--magenta)/0.06)] p-4 hover:glow-magenta flex items-start gap-2">
                <button onClick={() => openTool("ai", t)} data-testid={`ai-tool-apply-${t.id}`} className="text-left flex-1">
                  <Robot size={24} weight="duotone" className="text-[hsl(var(--magenta))]" />
                  <div className="font-semibold mt-1">{t.name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{t.explanation}</div>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger data-testid={`ai-tool-menu-${t.id}`} className="p-1 text-muted-foreground hover:text-foreground">
                    <DotsThreeVertical size={18} weight="bold" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-popover border-border">
                    <DropdownMenuItem data-testid={`ai-tool-modify-${t.id}`} onClick={() => { setModifyTool(t); setModifyText(""); }}><Sparkle size={14} className="mr-2" /> Modifica con AI</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`ai-tool-rename-${t.id}`} onClick={() => { setRenameTool(t); setRenameVal(t.name); }}><PencilSimple size={14} className="mr-2" /> Rinomina</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`ai-tool-duplicate-${t.id}`} onClick={() => duplicateTool(t)}><Copy size={14} className="mr-2" /> Duplica</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`ai-tool-save-lib-${t.id}`} onClick={() => { setSaveLibTool(t); setSaveLibName(t.name); }}><FloppyDisk size={14} className="mr-2" /> Salva in libreria</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`ai-tool-delete-${t.id}`} onClick={() => deleteTool(t)} className="text-destructive"><TrashSimple size={14} className="mr-2" /> Elimina</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {library.length > 0 && (!searching || libraryF.length > 0) && (
        <div className="mb-6" data-testid="tool-library">
          <button onClick={() => toggleWrap("library")} data-testid="wrap-library"
            className="w-full text-xs font-semibold text-[hsl(var(--gold))] mb-2 flex items-center gap-1"><Package size={14} /> Libreria tool salvati
            <CaretDown size={12} className={`ml-1 transition-transform ${(searching || isWrapOpen("library")) ? "" : "-rotate-90"}`} />
          </button>
          {(searching || isWrapOpen("library")) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {libraryF.map((t) => (
              <div key={t.id} data-testid={`lib-tool-${t.id}`} className="rounded-xl border border-border bg-card p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{t.tool?.explanation || t.tool?.name}</div>
                </div>
                {(() => { const dup = (data.ai_tools || []).some((a) => a.name === (t.tool?.name || t.name)); return (
                <Button size="sm" variant="outline" disabled={dup} data-testid={`lib-import-${t.id}`}
                  title={dup ? "Già importato in questo lavoro" : "Importa nel lavoro corrente"}
                  onClick={() => importFromLibrary(t)}>{dup ? "Importato" : "Importa"}</Button>
                ); })()}
                <button data-testid={`lib-delete-${t.id}`} onClick={() => deleteFromLibrary(t)} className="text-muted-foreground hover:text-destructive"><TrashSimple size={16} /></button>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-[hsl(var(--magenta)/0.4)] p-4 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Robot size={22} weight="duotone" className="text-[hsl(var(--magenta))]" />
          <h3 className="font-head font-bold">Crea un tool con l'AI</h3>
          <span className="text-xs text-muted-foreground">Claude Sonnet 4.6</span>
        </div>
        {aiText.startsWith("=") ? (
          <FormulaBuilder value={aiText} setValue={setAiText} columns={pickColumns} onApply={applyFormula} busy={aiLoading} />
        ) : (
        <div className="flex gap-2">
          <Textarea value={aiText} onChange={(e) => setAiText(e.target.value)} rows={2}
            data-testid="ai-tool-input"
            placeholder='Es: "confronta la somma di F1_Premio con F2_Importo per ogni F1_CF" oppure inizia con = per una formula'
            className="bg-muted resize-none" />
          <Button onClick={runAI} disabled={aiLoading} data-testid="ai-tool-submit-btn"
            className="bg-[hsl(var(--magenta))] hover:bg-[hsl(320,75%,40%)] text-white gap-2 self-stretch">
            {aiLoading ? "..." : <PaperPlaneRight size={18} weight="fill" />}
          </Button>
        </div>
        )}
        {!aiText.startsWith("=") && <p className="text-[11px] text-muted-foreground mt-1">Suggerimento: inizia con <strong>=</strong> per scrivere una formula stile Excel (es. =SE(&#123;colonna&#125; &lt; 100; "Basso"; &#123;colonna&#125;)).</p>}

        <div className="mt-4 space-y-2">
          {feed.map((item, i) => (
            <div key={i} className="fade-up">
              {item.type === "user" && (
                <div className="text-sm bg-primary/10 border border-primary/30 rounded-lg px-3 py-2">
                  <span className="text-primary text-xs font-semibold">Tu · </span>{item.text}
                </div>
              )}
              {item.type === "ai" && (
                <div className={`text-sm rounded-lg px-3 py-2 border flex items-start gap-2 ${item.error ? "badge-red_flag" : "bg-[hsl(var(--magenta)/0.1)] border-[hsl(var(--magenta)/0.35)]"}`}>
                  <Robot size={16} className="mt-0.5 text-[hsl(var(--magenta))]" />
                  <span>{item.text}</span>
                </div>
              )}
              {item.type === "verify" && <SummaryLine summary={item.summary} />}
            </div>
          ))}
        </div>
      </div>

      <ToolDialog tool={active?.def} open={open} setOpen={setOpen} columns={pickColumns} onRun={onDialogRun} activeCount={activeCount} />

      <Dialog open={!!modifyTool} onOpenChange={(o) => { if (!o) setModifyTool(null); }}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={20} className="text-[hsl(var(--magenta))]" /> Modifica "{modifyTool?.name}"</DialogTitle>
            <DialogDescription>Descrivi come cambiare lo strumento. L'AI riceve la funzione attuale e le richieste precedenti per raffinarlo.</DialogDescription>
          </DialogHeader>
          <Textarea value={modifyText} onChange={(e) => setModifyText(e.target.value)} rows={3} className="bg-muted resize-none"
            data-testid="ai-modify-input" placeholder='Es: "aggiungi anche la percentuale" o "evidenzia in rosso i valori diversi"' autoFocus />
          <DialogFooter><Button onClick={submitModify} disabled={aiLoading} data-testid="ai-modify-submit-btn">Aggiorna con AI</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTool} onOpenChange={(o) => { if (!o) setRenameTool(null); }}>
        <DialogContent className="bg-popover border-border max-w-md">
          <DialogHeader><DialogTitle>Rinomina tool</DialogTitle><DialogDescription>Assegna un nuovo nome allo strumento.</DialogDescription></DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="bg-muted" autoFocus
            data-testid="ai-rename-input" onKeyDown={(e) => e.key === "Enter" && submitRename()} />
          <DialogFooter><Button onClick={submitRename} data-testid="ai-rename-submit-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!saveLibTool} onOpenChange={(o) => { if (!o) setSaveLibTool(null); }}>
        <DialogContent className="bg-popover border-border max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FloppyDisk size={18} /> Salva tool in libreria</DialogTitle>
            <DialogDescription>Il tool sarà riutilizzabile in qualsiasi lavoro.</DialogDescription></DialogHeader>
          <Input value={saveLibName} onChange={(e) => setSaveLibName(e.target.value)} className="bg-muted" autoFocus
            data-testid="lib-save-name-input" onKeyDown={(e) => e.key === "Enter" && saveToLibrary()} />
          <DialogFooter><Button onClick={saveToLibrary} data-testid="lib-save-submit-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
